import React from 'react';
import { styles } from './styles';
import { toBlock, toRuns } from '../../legal/parser';
import type { ContentBlock, DocumentContent, LegalDocument, RichText } from '../../legal/interfaces';

// Renders one parsed LegalDocument. Shared by /legal (PlayerCafe, inside SandwichContainers)
// and /privacy (Novaeon, rendered directly), so both documents look and behave identically.
//
// The document's `title` is NOT rendered here — the consumer decides where it goes, because
// /legal puts it in the collapsed row header while /privacy uses it as the page heading.
// `version` is not rendered either: it is the acceptance contract with the app, and a raw ISO
// timestamp reads badly in a document header.

const Runs: React.FC<{ text: RichText }> = ({ text }) => (
  <>
    {toRuns(text).map((run, index) => {

      const content = run.strong
        ? <strong style={styles.strong}>{run.text}</strong>
        : run.text;

      if (!run.href) { return <React.Fragment key={index}>{content}</React.Fragment>; }

      // Anything leaving the site opens in a new tab, matching what the hand-written privacy
      // page did. `noreferrer` also covers `noopener`.
      const external = /^https?:\/\//.test(run.href);

      return (
        <a
          key={index}
          href={run.href}
          style={styles.link}
          {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
        >
          {content}
        </a>
      );
    })}
  </>
);

const Block: React.FC<{ block: ContentBlock }> = ({ block }) => {

  const resolved = toBlock(block);

  switch (resolved.type) {

    case 'subheading':
      return <p style={styles.subheading}>{resolved.text}</p>;

    case 'list':
      return (
        <ul style={styles.list}>
          {resolved.items.map((item, index) => (
            <li key={index} style={styles.listItem}>
              <span style={styles.bullet} aria-hidden="true" />
              <Runs text={item} />
            </li>
          ))}
        </ul>
      );

    default:
      return <p style={styles.paragraph}><Runs text={resolved.text} /></p>;
  }
};

const Section: React.FC<{ section: DocumentContent; isFirst: boolean }> = ({ section, isFirst }) => (
  <>
    <h2 style={isFirst ? { ...styles.heading, ...styles.headingFirst } : styles.heading}>
      {section.heading}
    </h2>
    {section.body.map((block, index) => <Block key={index} block={block} />)}
  </>
);

const LegalDocumentBody: React.FC<{ document: LegalDocument }> = ({ document: legalDoc }) => (
  <div>
    <p style={styles.effective}>Effective {legalDoc.effectiveDate}</p>
    <p style={styles.intro}>{legalDoc.intro}</p>
    {legalDoc.sections.map((section, index) => (
      <Section key={section.heading} section={section} isFirst={index === 0} />
    ))}
  </div>
);

export default LegalDocumentBody;
